import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { styleForFeature, pointToLayerForFeature } from '../lib/symbology.js';
import { BASEMAPS, DEFAULT_BASEMAP } from '../lib/basemaps.js';

function makeTileLayer(key) {
  const cfg = BASEMAPS[key] || BASEMAPS[DEFAULT_BASEMAP];
  const opts = {
    maxZoom: cfg.maxZoom,
    attribution: cfg.attribution,
    crossOrigin: true,
  };
  if (cfg.subdomains) opts.subdomains = cfg.subdomains;
  return L.tileLayer(cfg.url, opts);
}

/**
 * Leaflet map + GeoJSON layer. Re-fits view whenever `data` changes, swaps
 * basemap when `basemap` changes. Hands the Leaflet map instance to the
 * parent via `onMapReady` so the parent can drive PDF capture (fitBounds +
 * tile wait) without reaching into internals.
 */
export default function MapView({ data, center, basemap = DEFAULT_BASEMAP, onMapReady }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const tileRef = useRef(null);
  const layerRef = useRef(null);

  // Init map once.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView(center, 13);

    tileRef.current = makeTileLayer(basemap).addTo(map);

    L.control.scale({ imperial: true, metric: false, position: 'bottomright' }).addTo(map);
    mapRef.current = map;
    onMapReady?.(map);

    return () => {
      map.remove();
      mapRef.current = null;
      tileRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Basemap switch.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (tileRef.current) tileRef.current.remove();
    tileRef.current = makeTileLayer(basemap).addTo(map);
  }, [basemap]);

  // GeoJSON layer.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
    }
    const layer = L.geoJSON(data, {
      style: styleForFeature,
      pointToLayer: pointToLayerForFeature,
      onEachFeature: (feature, lyr) => {
        const p = feature.properties || {};
        if (p.label) lyr.bindTooltip(p.label, { direction: 'top', offset: [0, -6] });
      },
    }).addTo(map);
    layerRef.current = layer;
    try {
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      }
    } catch {
      map.setView(center, 13);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapRef.current || !center) return;
    mapRef.current.setView(center, mapRef.current.getZoom());
  }, [center && center[0], center && center[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div ref={containerRef} className="absolute inset-0" />
      <div
        aria-hidden="true"
        className="absolute bottom-3 left-3 z-[500] bg-white/95 border border-atlas-line rounded-sm flex flex-col items-center justify-center text-atlas-navy"
        style={{ width: 36, height: 44, fontSize: 10, lineHeight: 1 }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>▲</span>
        <span style={{ fontWeight: 600, marginTop: 2 }}>N</span>
      </div>
    </>
  );
}
